import {useEffect, useRef} from 'react';

const keyHandlerMap = new WeakMap();
const PROXY_BUTTON_CLASS = 'kl-zip-info-a11y-button';

const LOGO_SELECTORS = ['.zip-logo', 'img.zip-logo'];
const INFO_SELECTORS = [
  '.zip-learn-more',
  '[class*="learn-more"]',
  '.zip-info-icon',
  '[class*="zip-info"]',
  '.zip-container svg',
];

function collectQueryRoots(root) {
  const roots = new Set([root]);

  root.querySelectorAll('*').forEach((element) => {
    if (element.shadowRoot) {
      collectQueryRoots(element.shadowRoot).forEach((shadowRoot) => roots.add(shadowRoot));
    }
  });

  return [...roots];
}

function queryFirst(roots, selectors) {
  for (const selector of selectors) {
    for (const queryRoot of roots) {
      const match = queryRoot.querySelector(selector);

      if (match) {
        return match;
      }
    }
  }

  return null;
}

function findInfoControl(roots, logo) {
  for (const selector of INFO_SELECTORS) {
    for (const queryRoot of roots) {
      const match = queryRoot.querySelector(selector);

      if (match && match !== logo) {
        return match;
      }
    }
  }

  for (const queryRoot of roots) {
    for (const slot of queryRoot.querySelectorAll('slot')) {
      const slotClass = slot.className?.toString?.() ?? '';

      if (!slotClass.includes('learn-more') && !slotClass.includes('info')) {
        continue;
      }

      const assigned =
        typeof slot.assignedElements === 'function' ?
          slot.assignedElements({flatten: true}) :
          [];

      if (assigned[0] && assigned[0] !== logo) {
        return assigned[0];
      }

      return slot;
    }
  }

  return null;
}

function isFocusableTarget(element) {
  return (
    element &&
    element.nodeType === Node.ELEMENT_NODE &&
    !(element instanceof HTMLSlotElement)
  );
}

function stripInvalidSlotA11y(slot) {
  slot.removeAttribute('tabindex');
  slot.removeAttribute('role');
  slot.removeAttribute('aria-label');
  delete slot.dataset.klA11yEnhanced;
}

/**
 * `<slot>` cannot join the tab order even with tabindex — replace with a real button
 * that mirrors the icon and forwards activation to the Zip control.
 */
function ensureInfoProxyButton(slot, ariaLabel, enhancedElements) {
  if (!(slot instanceof HTMLSlotElement)) {
    return null;
  }

  stripInvalidSlotA11y(slot);

  const container = slot.parentElement;

  if (!container) {
    return null;
  }

  let button = container.querySelector(`button.${PROXY_BUTTON_CLASS}`);

  if (!button) {
    button = document.createElement('button');
    button.type = 'button';
    button.className = PROXY_BUTTON_CLASS;
    button.textContent = (slot.textContent || 'ⓘ').trim();
    slot.insertAdjacentElement('afterend', button);
  }

  slot.setAttribute('aria-hidden', 'true');
  slot.hidden = true;

  enhanceZipControl(button, ariaLabel, () => {
    slot.dispatchEvent(new MouseEvent('click', {bubbles: true, cancelable: true}));
    activateZipControl(slot);
  });
  enhancedElements.add(button);

  return button;
}

function removeInfoProxyButton(slot) {
  if (!(slot instanceof HTMLSlotElement)) {
    return;
  }

  const button = slot.parentElement?.querySelector(`button.${PROXY_BUTTON_CLASS}`);

  if (button) {
    removeZipControlEnhancement(button);
    button.remove();
  }

  slot.removeAttribute('aria-hidden');
  slot.hidden = false;
  stripInvalidSlotA11y(slot);
}

const enhanceZipControl = (element, ariaLabel, onActivate) => {
  if (!isFocusableTarget(element) || element.dataset.klA11yEnhanced === 'true') {
    return;
  }

  element.dataset.klA11yEnhanced = 'true';

  if (!element.hasAttribute('tabindex')) {
    element.setAttribute('tabindex', '0');
  }

  if (!element.getAttribute('role')) {
    element.setAttribute('role', 'button');
  }

  if (ariaLabel) {
    element.setAttribute('aria-label', ariaLabel);
  }

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    onActivate(element);
  };

  keyHandlerMap.set(element, handleKeyDown);
  element.addEventListener('keydown', handleKeyDown);
};

const removeZipControlEnhancement = (element) => {
  const handleKeyDown = keyHandlerMap.get(element);

  if (handleKeyDown) {
    element.removeEventListener('keydown', handleKeyDown);
    keyHandlerMap.delete(element);
  }
};

const activateZipControl = (element) => {
  element.click?.();

  const clickableParent = element.closest('.zip-container, .zip-widget__wrapper, [data-zm-widget]');

  clickableParent?.click?.();
};

/**
 * Makes Zip on-site messaging logo + info icon keyboard focusable after third-party inject.
 */
export function useZipWidgetA11y(containerRef, {enabled, logoLabel, infoLabel}) {
  const labelsRef = useRef({logoLabel, infoLabel});

  labelsRef.current = {logoLabel, infoLabel};

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return undefined;
    }

    const root = containerRef?.current;

    if (!root) {
      return undefined;
    }

    const enhancedElements = new Set();

    const applyEnhancements = () => {
      const zipRoot = root.querySelector('[data-zm-widget]') ?? root;
      const queryRoots = collectQueryRoots(zipRoot);
      const logo = queryFirst(queryRoots, LOGO_SELECTORS);
      const infoIcon = findInfoControl(queryRoots, logo);

      if (logo) {
        enhanceZipControl(logo, labelsRef.current.logoLabel, activateZipControl);
        enhancedElements.add(logo);
      }

      if (infoIcon instanceof HTMLSlotElement) {
        ensureInfoProxyButton(infoIcon, labelsRef.current.infoLabel, enhancedElements);
      } else if (infoIcon) {
        enhanceZipControl(infoIcon, labelsRef.current.infoLabel, activateZipControl);
        enhancedElements.add(infoIcon);
      }
    };

    applyEnhancements();

    const observer = new MutationObserver(applyEnhancements);

    observer.observe(root, {childList: true, subtree: true});

    return () => {
      observer.disconnect();
      enhancedElements.forEach((element) => {
        if (element.classList?.contains(PROXY_BUTTON_CLASS)) {
          removeZipControlEnhancement(element);
          element.remove();

          return;
        }

        if (element instanceof HTMLSlotElement) {
          removeInfoProxyButton(element);

          return;
        }

        removeZipControlEnhancement(element);
      });
    };
  }, [containerRef, enabled, logoLabel, infoLabel]);
}
